import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getMyProfile, changeMyPassword } from '../services/graphService';
import { profileRequest } from '../msalConfig';
import './ProfilePage.css';

export const ProfilePage = () => {
  const { user, isAdmin, isMember, getAccessToken } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const token = await getAccessToken(profileRequest.scopes);
        const data = await getMyProfile(token);
        setProfile(data);
      } catch (err) {
        console.error('Failed to fetch profile:', err);
      } finally {
        setLoadingProfile(false);
      }
    };
    fetchProfile();
  }, [getAccessToken]);

  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 5000);
  };

  const getRoleBadge = () => {
    if (isAdmin) return <span className="profile-badge badge-admin">Admin</span>;
    if (isMember) return <span className="profile-badge badge-member">Member</span>;
    return <span className="profile-badge badge-guest">Guest</span>;
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (newPassword.length < 8) {
      showMessage('New password must be at least 8 characters', 'error');
      return;
    }
    if (newPassword !== confirmPassword) {
      showMessage('New passwords do not match', 'error');
      return;
    }
    if (currentPassword === newPassword) {
      showMessage('New password must be different from current password', 'error');
      return;
    }

    setChangingPassword(true);
    try {
      const token = await getAccessToken(profileRequest.scopes);
      await changeMyPassword(token, currentPassword, newPassword);
      showMessage('Password changed successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      showMessage(err.message || 'Failed to change password', 'error');
    } finally {
      setChangingPassword(false);
    }
  };

  if (loadingProfile) {
    return (
      <div className="profile-page">
        <div className="profile-container">
          <div className="profile-loading">
            <div className="spinner"></div>
            <p>Loading profile...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <div className="profile-container">
        <div className="profile-header-section">
          <h1>👤 My Profile</h1>
        </div>

        {message && (
          <div className={`profile-message profile-message-${message.type}`}>
            {message.type === 'success' ? '✅' : '❌'} {message.text}
          </div>
        )}

        {/* Profile Info Card */}
        <div className="profile-card profile-card-hero">
          <div className="profile-avatar">
            <span>{(user?.displayName || 'U')[0].toUpperCase()}</span>
          </div>
          <div className="profile-info">
            <h2>{profile?.displayName || user?.displayName || 'User'}</h2>
            <p className="profile-email">
              {profile?.mail || profile?.userPrincipalName || user?.email}
            </p>
            <div className="profile-role">{getRoleBadge()}</div>
          </div>
        </div>

        {/* Details Card */}
        <div className="profile-card">
          <h3>Account Details</h3>
          <div className="profile-details-grid">
            <div className="profile-detail">
              <label>Display Name</label>
              <span>{profile?.displayName || '—'}</span>
            </div>
            <div className="profile-detail">
              <label>Email</label>
              <span>{profile?.mail || profile?.userPrincipalName || '—'}</span>
            </div>
            <div className="profile-detail">
              <label>First Name</label>
              <span>{profile?.givenName || '—'}</span>
            </div>
            <div className="profile-detail">
              <label>Last Name</label>
              <span>{profile?.surname || '—'}</span>
            </div>
          </div>
        </div>

        {/* Change Password Card */}
        <div className="profile-card">
          <h3>🔒 Change Password</h3>
          <p className="profile-card-subtitle">
            Update your password. You'll need your current password to make the change.
          </p>
          <form onSubmit={handleChangePassword} className="password-form">
            <div className="form-group">
              <label htmlFor="currentPassword">Current Password</label>
              <input
                id="currentPassword"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
            </div>
            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
              />
              <small className="form-hint">Minimum 8 characters</small>
            </div>
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
              />
            </div>
            <button
              type="submit"
              className="btn btn-change-pw"
              disabled={changingPassword || !currentPassword || !newPassword || !confirmPassword}
            >
              {changingPassword ? 'Changing...' : 'Change Password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
